import { useState, useEffect } from 'react';
import { sortImagesByUploadDate, getImageRecencyScore } from './imageSorting';
import {
  COMPANY_INFO as FALLBACK_COMPANY_INFO,
  CORE_VALUES as FALLBACK_CORE_VALUES,
  SERVICES as FALLBACK_SERVICES,
  BEFORE_AFTER_PAIRS as FALLBACK_BEFORE_AFTER,
  TESTIMONIALS as FALLBACK_TESTIMONIALS,
  GALLERY_CATEGORIES as FALLBACK_GALLERY_CATEGORIES,
  MAIN_GALLERIES as FALLBACK_MAIN_GALLERIES,
  GALLERIES as FALLBACK_GALLERIES
} from '../data/siteData';

const CMS_BASE_URL = 'https://api.dev.auxo.dk/public/content';
const CMS_V2_BASE_URL = 'https://api.dev.auxo.dk/public/v2/content';

// Simple in-memory cache to avoid duplicate fetches across components
const cache = new Map();

export function clearCache(collectionName) {
  if (collectionName) {
    cache.delete(collectionName);
  } else {
    cache.clear();
  }
}

/**
 * Fetch and save before/after custom order via backend function
 */
export async function fetchBeforeAfterOrder() {
  try {
    const res = await fetch('/_fn/before-after-order');
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.order) ? data.order : [];
  } catch {
    return [];
  }
}

export async function saveBeforeAfterOrder(order) {
  const res = await fetch('/_fn/before-after-order', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ order }),
  });
  if (!res.ok) {
    throw new Error(`Kunne ikke gemme rækkefølge (${res.status})`);
  }
  return await res.json();
}

/**
 * Fetch raw collection from CMS with fallback
 */
export async function fetchCollection(collectionName) {
  if (cache.has(collectionName)) {
    return cache.get(collectionName);
  }
  try {
    const res = await fetch(`${CMS_BASE_URL}/${collectionName}`);
    if (!res.ok) {
      throw new Error(`Failed to fetch CMS collection: ${collectionName} (${res.status})`);
    }
    const data = await res.json();
    const items = Array.isArray(data) ? data : (data.items || []);
    cache.set(collectionName, items);
    return items;
  } catch (err) {
    console.warn(`[CMS] Using fallback for ${collectionName}:`, err.message);
    return null;
  }
}

/**
 * Helper to normalize image field from CMS (can be array or string)
 */
export function getImageUrl(assetField, fallbackUrl = '') {
  if (!assetField) return fallbackUrl;
  if (Array.isArray(assetField) && assetField.length > 0) {
    return assetField[0];
  }
  if (typeof assetField === 'string' && assetField.trim() !== '') {
    return assetField;
  }
  return fallbackUrl;
}

/**
 * Hook for Company Info
 */
export function useCompanyInfo() {
  const [info, setInfo] = useState(FALLBACK_COMPANY_INFO);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    fetchCollection('virksomhedsoplysninger').then((items) => {
      if (!isMounted) return;
      if (items && items.length > 0) {
        const item = items[0];
        setInfo({
          name: item.firmanavn || FALLBACK_COMPANY_INFO.name,
          previousName: item.tidligere_navn || FALLBACK_COMPANY_INFO.previousName,
          address: item.adresse || FALLBACK_COMPANY_INFO.address,
          zipCity: item.postnr_by || FALLBACK_COMPANY_INFO.zipCity,
          fullAddress: item.fuld_adresse || `${item.adresse || ''}, ${item.postnr_by || ''}`.trim() || FALLBACK_COMPANY_INFO.fullAddress,
          phone: item.telefon || FALLBACK_COMPANY_INFO.phone,
          phoneRaw: item.telefon_raw || (item.telefon ? item.telefon.replace(/\s+/g, '') : FALLBACK_COMPANY_INFO.phoneRaw),
          email: item.email || FALLBACK_COMPANY_INFO.email,
          cvr: item.cvr || FALLBACK_COMPANY_INFO.cvr,
          hours: item.aabningstider || FALLBACK_COMPANY_INFO.hours,
          slogan: item.slogan || FALLBACK_COMPANY_INFO.slogan,
          locationMapUrl: item.maps_url || FALLBACK_COMPANY_INFO.locationMapUrl,
          facebookPage: item.facebook_url || FALLBACK_COMPANY_INFO.facebookPage,
          facebookReviews: item.facebook_anmeldelser_url || FALLBACK_COMPANY_INFO.facebookReviews,
          trustpilotReviews: item.trustpilot_url || FALLBACK_COMPANY_INFO.trustpilotReviews,
          logo: getImageUrl(item.logo, "https://assets.auxodev.dk/assets/dde638d5-f83c-4036-8d58-4f922ae20635/image_f313bd29.png")
        });
      }
      setLoading(false);
    }).catch(() => {
      if (isMounted) setLoading(false);
    });

    return () => { isMounted = false; };
  }, []);

  return { companyInfo: info, loading };
}

/**
 * Hook for Core Values
 */
export function useCoreValues() {
  const [values, setValues] = useState(FALLBACK_CORE_VALUES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    fetchCollection('vaerdier').then((items) => {
      if (!isMounted) return;
      if (items && items.length > 0) {
        const sorted = [...items].sort((a, b) => (Number(a.sortering) || 99) - (Number(b.sortering) || 99));
        setValues(sorted.map((item) => ({
          title: item.titel || item.title || '',
          desc: item.beskrivelse || item.desc || '',
          icon: item.ikon || item.icon || 'fa-solid fa-check'
        })));
      }
      setLoading(false);
    }).catch(() => {
      if (isMounted) setLoading(false);
    });

    return () => { isMounted = false; };
  }, []);

  return { coreValues: values, loading };
}

/**
 * Hook for Testimonials / Reviews
 */
export function useReviews() {
  const [reviews, setReviews] = useState(FALLBACK_TESTIMONIALS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    fetchCollection('anmeldelser').then((items) => {
      if (!isMounted) return;
      if (items && items.length > 0) {
        const sorted = [...items].sort((a, b) => (Number(a.sortering) || 99) - (Number(b.sortering) || 99));
        setReviews(sorted.map((item) => ({
          author: item.forfatter || item.author || '',
          location: item.lokation || item.location || '',
          source: item.kilde || 'Trustpilot',
          rating: Number(item.stjerner) || 5,
          text: item.anmeldelse || item.text || ''
        })));
      }
      setLoading(false);
    }).catch(() => {
      if (isMounted) setLoading(false);
    });

    return () => { isMounted = false; };
  }, []);

  return { reviews, loading };
}

/**
 * Hook for Before/After Pairs
 */
export function useBeforeAfter() {
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    Promise.all([
      fetchCollection('foer_efter'),
      fetchBeforeAfterOrder()
    ]).then(([items, savedOrder]) => {
      if (!isMounted) return;
      if (items && items.length > 0) {
        const orderMap = new Map();
        if (Array.isArray(savedOrder) && savedOrder.length > 0) {
          savedOrder.forEach((id, index) => {
            orderMap.set(String(id), index);
          });
        }

        const sorted = [...items].sort((a, b) => {
          const idA = String(a.id || '');
          const idB = String(b.id || '');
          const hasA = orderMap.has(idA);
          const hasB = orderMap.has(idB);

          if (hasA && hasB) {
            return orderMap.get(idA) - orderMap.get(idB);
          }
          if (hasA && !hasB) return -1;
          if (!hasA && hasB) return 1;

          // Sortering af før/efter projekter fallback:
          // Prioritér projekternes billeders upload-dato (nyest upload først),
          // så nye før/efter-sager (f.eks. sep/okt 2026) automatisk ligger øverst.
          const urlA_after = getImageUrl(a.efter_billede || a.after_image, '');
          const urlA_before = getImageUrl(a.foer_billede || a.before_image, '');
          const scoreA = Math.max(getImageRecencyScore(urlA_after), getImageRecencyScore(urlA_before));

          const urlB_after = getImageUrl(b.efter_billede || b.after_image, '');
          const urlB_before = getImageUrl(b.foer_billede || b.before_image, '');
          const scoreB = Math.max(getImageRecencyScore(urlB_after), getImageRecencyScore(urlB_before));

          if (scoreA !== scoreB) return scoreB - scoreA;

          const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
          const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
          return dateB - dateA;
        });
        setPairs(
          sorted
            .map((item, idx) => ({
              id: item.id || `pair-${idx}`,
              title: item.titel || item.title || '',
              location: item.lokation || item.location || '',
              category: item.kategori || item.category || 'Belægning & Have',
              sortering: item.sortering,
              before: {
                url: getImageUrl(item.foer_billede || item.before_image, ''),
                caption: item.foer_tekst || item.before_text || `Før - ${item.titel || ''}`
              },
              after: {
                url: getImageUrl(item.efter_billede || item.after_image, ''),
                caption: item.efter_tekst || item.after_text || `Efter - ${item.titel || ''}`
              }
            }))
            .filter((p) => p.before.url && p.after.url)
        );
      } else {
        setPairs([]);
      }
      setLoading(false);
    }).catch(() => {
      if (isMounted) setLoading(false);
    });

    return () => { isMounted = false; };
  }, []);

  return { pairs, loading };
}

/**
 * Hook for Services
 */
export function useServices() {
  const [services, setServices] = useState(
    FALLBACK_SERVICES.map((s) => ({ ...s, primaryImage: '' }))
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    fetchCollection('ydelser').then((items) => {
      if (!isMounted) return;
      if (items && items.length > 0) {
        const sorted = [...items].sort((a, b) => (Number(a.sort_order) || 99) - (Number(b.sort_order) || 99));
        const mapped = sorted.map((item) => {
          const slug = item.url_slug || item.slug || '';
          const fallback = FALLBACK_SERVICES.find(s => s.slug === slug) || {};
          return {
            id: item.id || slug,
            slug: slug,
            title: item.title || fallback.title || '',
            category: item.category || fallback.category || 'Entreprenør',
            iconClass: item.icon_class || fallback.iconClass || 'fa-solid fa-trowel',
            shortDesc: item.description || fallback.shortDesc || '',
            primaryImage: getImageUrl(item.primary_image, ''),
            gallerySlug: item.gallery_slug || fallback.gallerySlug || 'opgaver',
            body: item.body || fallback.body || '',
            metaTitle: item.meta_title || fallback.metaTitle || `${item.title || ''} - CMSNord.dk`,
            metaDescription: item.meta_description || fallback.metaDescription || item.description || '',
            featuredHomepage: item.featured_homepage !== undefined ? Boolean(item.featured_homepage) : fallback.featuredHomepage
          };
        });
        setServices(mapped);
      }
      setLoading(false);
    }).catch(() => {
      if (isMounted) setLoading(false);
    });

    return () => { isMounted = false; };
  }, []);

  return { services, loading };
}

/**
 * Hook for Gallery Categories & Albums
 */
export function useGalleryCategories() {
  const [categories, setCategories] = useState(
    FALLBACK_GALLERY_CATEGORIES.map((c) => ({
      ...c,
      coverImage: '',
      images: []
    }))
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    fetchCollection('galleri').then((items) => {
      if (!isMounted) return;
      if (items && items.length > 0) {
        // Filter udgyldige URLs fra (f.eks. sammensmeltede filnavne som _31fbf82f_41ecfa7a eller manglende uploads)
        const validAssetSet = new Set([
          'image_903e5b13.png', 'image_22e40564.png', 'image_dcf2ddba.png', 'image_f6215a01.png',
          'image_9916d979.png', 'image_e0e1022a.png', 'image_91419577.png', 'image_1de0b751.png',
          'image_1472e371.png', 'image_95847a14.png', 'image_d69736c7.png', 'image_7befaa96.png',
          'image_9ca267ab.png', 'image_d6aefe4f.png', 'image_d2ebbb70.png', 'image_fbff57b9.png',
          'image_9cb6639c.png', 'image_a125de58.png', 'image_c18616e2.png', 'image_4a16eccb.png',
          'image_60805867.png', 'image_f4a4c0a7.png', 'image_3b1fa2bc.png', 'image_314a51ef.png',
          'image_5ee6df5a.png', 'image_191b4108.png', 'image_a1bce7ec.png', 'image_a9382b0d.png',
          'image_30fbe428.png', 'image_172fc4cc.png', 'image_93f752cd.png', 'image_c3d642df.png',
          'image_f6ffe5a5.png', 'image_1a2d9fa6.png', 'image_88343032.png', 'image_d2f57aac.png',
          'image_69c35847.png', 'image_88b9de22.png', 'image_4fb65f8a.png', 'image_e1c871dd.png',
          'image_1034c39d.png', 'image_068360e6.png', 'image_15e65ff6.png', 'image_08cec0a4.png',
          'image_b82eb960.png', 'image_e8dcc116.png', 'image_8e493a4b.png', 'image_938e69fb.png',
          'image_1dd6665e.png', 'image_2fdf91d1.png', 'image_ebf3a6ed.png', 'image_a5db7c61.png',
          'image_7b8e6561.png', 'image_1cce3522.png', 'image_68a42862.png', 'image_07baa89b.png',
          'image_2c8f4020.png', 'image_44f4b61f.png', 'image_f0408a33.png', 'image_0a4d7899.png',
          'image_81715a2c.png', 'image_3632d8e0.png', 'image_773b378f.png', 'image_814af61c.png',
          'image_ffd6753e.png', 'image_4a3d36ee.png', 'image_dd7eef86.png', 'image_1d6d037a.png',
          'image_dedb13bb.png', 'image_687ba43f.png', 'image_9c77c923.png', 'image_72c71d5e.png',
          'image_da20d39a.png', 'image_4aff9f42.png', 'image_f9b27e35.png', 'image_5e8b3f50.png',
          'image_fd830e42.png', 'image_4b028e73.png', 'image_f3911fc1.png', 'image_bd04dd7a.png',
          'image_4fc6b762.png', 'image_38093186.png', 'image_c4cbaed5.png', 'image_ea070cf9.png',
          'image_2e2f91d0.png', 'image_c90f6ad5.png', 'image_ba9497e1.png', 'image_8221b4bc.png',
          'image_6795666b.png', 'image_26f71c28.png', 'image_0ce0abe7.png', 'image_0de28e98.png',
          'image_2cfc6aa2.png', 'image_a6cd3e7d.png', 'image_fd472f63.png', 'image_9c12f5cb.png',
          'image_f6d1ee5c.png', 'image_5fbd33f1.png', 'image_af210fd6.png', 'image_6585ec47.png',
          'image_ce0d06cc.png', 'image_c0e26f9f.png', 'image_3e358a6b.png', 'image_c4229f80.png',
          'image_f313bd29.png', 'image.png'
        ]);

        const isRealAsset = (u) => {
          if (!u || typeof u !== 'string') return false;
          const filename = u.split('/').pop().toLowerCase();
          // Hvis det er et "image_*.png" upload, skal det matche de faktiske uploads i biblioteket
          if (filename.startsWith('image_') && filename.endsWith('.png')) {
            return validAssetSet.has(filename);
          }
          return true;
        };

        // Map alle CMS-album så både nye og eksisterende kategorier er fuldt tilgængelige
        const mappedFromCms = items.map((item) => {
          const slug = (item.url_slug || item.slug?._ || item.slug || '').toLowerCase().trim();
          const sysSlug = (item.slug?._ || item.slug || '').toLowerCase().trim();
          const rawImagesList = Array.isArray(item.images) ? item.images : (Array.isArray(item.billeder) ? item.billeder : []);
          const imagesList = rawImagesList.filter(isRealAsset);

          // Fjern dubletter (både identiske URLs og gentagne filnavne / hash-varianter af samme billede)
          // BEMÆRK: Billeder med navne som "image_*.png" / "image.png" må IKKE deduplikeres mod hinanden,
          // da over 70 forskellige unikke fotos oprindeligt blev uploadet med standardnavnet "image.png".
          const seenAssetKeys = new Set();
          const uniqueImagesList = imagesList.filter((url) => {
            if (!url || typeof url !== 'string') return false;
            const rawFilename = url.split('/').pop().toLowerCase();
            // Hvis det er et "image_*.png" eller "image.png", bevares det unikke filnavn
            const dedupeKey = rawFilename.startsWith('image_') || rawFilename.startsWith('image.')
              ? rawFilename
              : rawFilename.replace(/_[a-f0-9]{8,}(?=\.[a-z0-9]+$)/i, '');

            if (seenAssetKeys.has(dedupeKey)) {
              return false;
            }
            seenAssetKeys.add(dedupeKey);
            return true;
          });
            
          const mappedImages = sortImagesByUploadDate(
            uniqueImagesList.map((url) => ({
              url,
              caption: item.title || item.titel || ''
            }))
          );

          return {
            id: slug || sysSlug || item.id,
            slug: slug || sysSlug,
            sysSlug,
            rawId: item.id,
            title: item.title || item.titel || '',
            category: item.category || '',
            description: item.description || item.beskrivelse || '',
            coverImage: getImageUrl(item.cover_image || item.cover_billede, (mappedImages[0] && mappedImages[0].url) || ''),
            images: mappedImages
          };
        });

        // Sørg for at alle forventede fallback-kategorier også findes hvis de efterspørges
        const allCategories = [...mappedFromCms];
        FALLBACK_GALLERY_CATEGORIES.forEach((fallback) => {
          if (fallback.id === 'foer-efter' || fallback.slug === 'foer-efter') return;
          if (!allCategories.some((c) => c.slug === fallback.slug || c.id === fallback.id)) {
            allCategories.push({
              ...fallback,
              coverImage: '',
              images: []
            });
          }
        });

        setCategories(allCategories);
      } else {
        setCategories(FALLBACK_GALLERY_CATEGORIES.map((c) => ({ ...c, coverImage: '', images: [] })));
      }
      setLoading(false);
    }).catch(() => {
      if (isMounted) setLoading(false);
    });

    return () => { isMounted = false; };
  }, []);

  return { categories, loading };
}

export function useGalleries() {
  return useGalleryCategories();
}

export function getServiceGalleryData(service, categories = []) {
  if (!service) return { title: 'Galleri', images: [] };

  const gallerySlug = (service.gallerySlug || '').toLowerCase().trim();
  const serviceSlug = (service.slug || '').toLowerCase().trim();
  const serviceCategory = (service.category || '').toLowerCase().trim();

  // Find matchende kategori i categories
  let matchedCat = categories.find((c) => {
    const cid = (c.id || '').toLowerCase().trim();
    const cslug = (c.slug || '').toLowerCase().trim();
    return cid === gallerySlug || cslug === gallerySlug;
  });

  if (!matchedCat) {
    if (gallerySlug === 'jordarbejde' || serviceSlug.includes('grave') || serviceCategory.includes('entreprenør')) {
      matchedCat = categories.find((c) => c.id === 'jordarbejde');
    } else if (gallerySlug === 'nedbrydning' || serviceSlug.includes('nedbryd')) {
      matchedCat = categories.find((c) => c.id === 'nedbrydning');
    } else if (gallerySlug === 'belaegning' || serviceSlug.includes('belaeg')) {
      matchedCat = categories.find((c) => c.id === 'belaegning');
    } else if (gallerySlug === 'anlaegsarbejde' || serviceSlug.includes('rydning') || serviceSlug.includes('anlaeg')) {
      matchedCat = categories.find((c) => c.id === 'anlaegsarbejde');
    } else if (gallerySlug === 'haveservice' || serviceSlug.includes('have')) {
      matchedCat = categories.find((c) => c.id === 'haveservice');
    } else if (gallerySlug === 'containerudlejning' || serviceSlug.includes('container')) {
      matchedCat = categories.find((c) => c.id === 'containerudlejning');
    } else if (gallerySlug === 'vognmandskoersel' || serviceSlug.includes('vognmand') || serviceSlug.includes('materiale') || serviceSlug.includes('bortskaffelse')) {
      matchedCat = categories.find((c) => c.id === 'vognmandskoersel');
    }
  }

  const images = matchedCat && matchedCat.images ? sortImagesByUploadDate(matchedCat.images) : [];
  return {
    title: matchedCat?.title || `Billeder fra ${service.title}`,
    description: matchedCat?.description || '',
    categorySlug: matchedCat?.slug || matchedCat?.id || 'all',
    images // allerede sorteret med nyeste først
  };
}

export function usePageData(urlSlug, fallbackData = {}) {
  const [page, setPage] = useState({
    ...fallbackData,
    heroImage: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    fetchCollection('sider').then((items) => {
      if (!isMounted) return;
      if (items && items.length > 0) {
        const found = items.find((p) => p.url_slug === urlSlug || p.slug?._ === urlSlug);
        if (found) {
          setPage({
            title: found.title || fallbackData.title || '',
            menuTitle: found.menu_title || fallbackData.menuTitle || found.title || '',
            urlSlug: found.url_slug || urlSlug,
            heroSubtitle: found.hero_subtitle || fallbackData.heroSubtitle || '',
            heroVideo: found.hero_video || '',
            heroImage: getImageUrl(found.hero_image, ''),
            introduction: found.introduction || fallbackData.introduction || '',
            body: found.body || fallbackData.body || '',
            gallerySlug: found.gallery_slug || fallbackData.gallerySlug || 'opgaver',
            metaTitle: found.meta_title || fallbackData.metaTitle || '',
            metaDescription: found.meta_description || fallbackData.metaDescription || ''
          });
        }
      }
      setLoading(false);
    }).catch(() => {
      if (isMounted) setLoading(false);
    });

    return () => { isMounted = false; };
  }, [urlSlug]);

  return { page, loading };
}
